import React, { useState } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline, sfSub } from './sectionHelpers';

interface Props {
  section: SectionInstance;
}

const ContactSplit: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const [submitted, setSubmitted] = useState(false);

  const formFields = (c.form_fields || [
    { label: 'Full Name', type: 'text', required: true },
    { label: 'Email Address', type: 'email', required: true },
    { label: 'Phone Number', type: 'tel', required: false },
    { label: 'Message', type: 'textarea', required: true },
  ]) as Array<{ label: string; type: string; required: boolean }>;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: 'var(--brand-btn-radius, 6px)',
    border: '1px solid rgba(0,0,0,0.15)',
    fontSize: '0.95rem',
    background: '#fff',
    color: 'var(--brand-text, #1e293b)',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  return (
    <section className="sb-render sb-render-contact-split" style={{ padding: '80px 24px', background: 'var(--brand-bg, #fff)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexWrap: 'wrap', gap: 48, alignItems: 'flex-start' }}>

        {/* Left: Company details */}
        <div style={{ flex: '1 1 340px' }}>
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.25rem', fontWeight: 700, marginBottom: 12, letterSpacing: '-0.025em' }}
          >
            {sfHeadline(c, 'Get in Touch')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.05rem', color: 'var(--brand-text-light, #64748b)', marginBottom: 40, lineHeight: 1.7 }}
          >
            {sfSub(c, "We'd love to hear from you. Send us a message and we'll get back to you as soon as possible.")}
          </InlineEditText>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {c.email && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: 'rgba(37,99,235,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.2rem', flexShrink: 0,
                }}>
                  ✉️
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--brand-text-light, #64748b)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Email</div>
                  <a href={`mailto:${c.email}`} style={{ color: 'var(--brand-primary, #2563eb)', textDecoration: 'none', fontWeight: 500 }}>{c.email}</a>
                </div>
              </div>
            )}
            {c.phone && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: 'rgba(37,99,235,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.2rem', flexShrink: 0,
                }}>
                  📞
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--brand-text-light, #64748b)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Phone</div>
                  <a href={`tel:${c.phone}`} style={{ color: 'var(--brand-primary, #2563eb)', textDecoration: 'none', fontWeight: 500 }}>{c.phone}</a>
                </div>
              </div>
            )}
            {c.address && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: 'rgba(37,99,235,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1.2rem', flexShrink: 0,
                }}>
                  📍
                </div>
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--brand-text-light, #64748b)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>Address</div>
                  <span style={{ fontWeight: 500 }}>{c.address}</span>
                </div>
              </div>
            )}
          </div>

          {/* Map embed */}
          {c.map_embed_url && (
            <div style={{ marginTop: 32, borderRadius: 'var(--brand-card-radius, 12px)', overflow: 'hidden', height: 200, border: '1px solid rgba(0,0,0,0.1)' }}>
              <iframe
                src={c.map_embed_url}
                width="100%"
                height="200"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Map"
              />
            </div>
          )}
        </div>

        {/* Right: Form */}
        <div style={{
          flex: '1 1 400px',
          background: 'var(--brand-bg-alt, #f8fafc)',
          borderRadius: 'var(--brand-card-radius, 16px)',
          padding: '40px 36px',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          {submitted ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div style={{ fontSize: '3rem', marginBottom: 16 }}>✅</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 8 }}>Message Sent!</h3>
              <p style={{ color: 'var(--brand-text-light, #64748b)' }}>We'll get back to you shortly.</p>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {formFields.map((field, i) => (
                <div key={i}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: 6, color: 'var(--brand-text, #1e293b)' }}>
                    {field.label}
                    {field.required && <span style={{ color: 'var(--brand-primary, #2563eb)', marginLeft: 3 }}>*</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      required={field.required}
                      rows={4}
                      style={{ ...inputStyle, resize: 'vertical' }}
                      placeholder={`Enter your ${field.label.toLowerCase()}`}
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      required={field.required}
                      style={inputStyle}
                      placeholder={`Enter your ${field.label.toLowerCase()}`}
                    />
                  )}
                </div>
              ))}
              <button
                type="submit"
                style={{
                  padding: '14px 32px',
                  background: 'var(--brand-primary, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 'var(--brand-btn-radius, 6px)',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer',
                  marginTop: 4,
                  transition: 'background 0.2s',
                  fontFamily: 'inherit',
                }}
              >
                Send Message
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactSplit;
